import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Lock, User } from 'lucide-react';
import config from '../../../configLoader';
import { useSiteContext } from '../../../SiteContext';
import { Page } from '../../../interfaces/PagesInterface';
import type { EmployeePortalConfig } from '../../../interfaces/ConfigInterface';
import {
  describePortalError,
  getPortalHolidays,
  getPortalPayPeriodsAround,
  getPortalPeriodHourly,
  getPortalSelf,
  getPortalTimeOffRequests,
  getPortalTimesheetStatus,
  reacquireTokenInteractive,
} from '../../../api/portal';
import type { PortalErrorInfo } from './portalErrors';
import { currentAndPrevious, pendingTimeOffCount } from './portalModel';
import { addDaysYmd, localYmd } from './portalTime';
import type {
  PortalHoliday,
  PortalHourlyRow,
  PortalPayPeriod,
  PortalSelf,
  PortalTimeOffRequest,
  PortalTimesheetStatus,
} from './portalTypes';
import './employeePortal.css';

/**
 * Employee Time Portal — the shell-level data every My Time page shares (P-TAS-20 Phase 3).
 * Mounted by LayoutV2's EmployeeShellBody, so it ONLY calls /portal/* (never /admin/*,
 * which refuses an employee-only user). Loads the employee (/portal/self), the pay periods
 * around today, this week's and last week's timesheet status, time off requests and the
 * coming holidays; feeds the sidebar badges; and owns the non-dismissable "Your session
 * expired" modal (a 401 anywhere in the portal lands here).
 */

const HOLIDAY_LOOKAHEAD_DAYS = 120;

export interface PortalData {
  cfg: EmployeePortalConfig;
  zone: string;
  /** Today's local date in the tenant zone (refreshed on every load). */
  todayYmd: string;
  self: PortalSelf | null;
  /** The first load hasn't finished. */
  loading: boolean;
  /** Why /portal/self failed (null when it loaded). */
  loadError: PortalErrorInfo | null;
  periods: PortalPayPeriod[];
  currentPeriod: PortalPayPeriod | null;
  previousPeriod: PortalPayPeriod | null;
  thisWeekStatus: PortalTimesheetStatus | null;
  lastWeekStatus: PortalTimesheetStatus | null;
  /** Last week has punches and is neither submitted nor exported. */
  lastWeekOpen: boolean;
  requests: PortalTimeOffRequest[];
  holidays: PortalHoliday[];
  /** Reload everything (the strip's Retry, after a lock refusal, after the session is restored). */
  reload: () => Promise<void>;
  /** Reload just the employee and this week's status (after a punch). */
  reloadSelf: () => Promise<void>;
  /**
   * Reload the time-off requests (Time Off's list, Coming up and the Time Off badge read them).
   * A 401 opens the session modal; any failure rejects so the caller can show it.
   */
  reloadTimeOffRequests: () => Promise<void>;
  sessionExpired: boolean;
  /** Show the session-expired modal. `note` names what is safe (e.g. the unsent punch). */
  reportSessionExpired: (note?: string) => void;
  /** Called after the user signs in again from the modal; returns an unsubscribe. */
  onSessionRestored: (cb: () => void) => () => void;
  /** A request to show a given week on My Timesheet (e.g. Today's "Review & submit"), consumed by the page. */
  timesheetIntent: TimesheetIntent | null;
  /** Go to My Timesheet on `payPeriodID`, optionally opening the submit wizard. */
  openTimesheet: (payPeriodID: number, openSubmit?: boolean) => void;
  clearTimesheetIntent: () => void;
}

export interface TimesheetIntent {
  payPeriodID: number;
  openSubmit: boolean;
}

const PortalDataContext = createContext<PortalData | null>(null);

export function usePortalData(): PortalData {
  const ctx = useContext(PortalDataContext);
  if (!ctx) throw new Error('usePortalData must be used inside <PortalProvider>');
  return ctx;
}

export const PortalProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { setPortalBadges, setPage } = useSiteContext();
  const cfg = config.employeePortal as EmployeePortalConfig;
  const zone = cfg?.timeZone || 'America/Los_Angeles';

  const [todayYmd, setTodayYmd] = useState(() => localYmd(Date.now(), zone));
  const [self, setSelf] = useState<PortalSelf | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<PortalErrorInfo | null>(null);
  const [periods, setPeriods] = useState<PortalPayPeriod[]>([]);
  const [thisWeekStatus, setThisWeekStatus] = useState<PortalTimesheetStatus | null>(null);
  const [lastWeekStatus, setLastWeekStatus] = useState<PortalTimesheetStatus | null>(null);
  const [lastWeekHasTime, setLastWeekHasTime] = useState(false);
  const [requests, setRequests] = useState<PortalTimeOffRequest[]>([]);
  const [holidays, setHolidays] = useState<PortalHoliday[]>([]);

  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionNote, setSessionNote] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [signInFailed, setSignInFailed] = useState(false);
  const restoredListeners = useRef(new Set<() => void>());
  const [timesheetIntent, setTimesheetIntent] = useState<TimesheetIntent | null>(null);

  const openTimesheet = useCallback((payPeriodID: number, openSubmit = false) => {
    setTimesheetIntent({ payPeriodID, openSubmit });
    setPage(Page.MyTimesheet);
  }, [setPage]);
  const clearTimesheetIntent = useCallback(() => setTimesheetIntent(null), []);

  const reportSessionExpired = useCallback((note?: string) => {
    setSessionNote(note ?? null);
    setSignInFailed(false);
    setSessionExpired(true);
  }, []);

  /** Runs a portal read; a 401 opens the session modal and the read resolves to `fallback`. */
  const guarded = useCallback(async <T,>(fn: () => Promise<T>, fallback: T): Promise<T> => {
    try {
      return await fn();
    } catch (err) {
      const info = describePortalError(err);
      if (info.kind === 'sessionExpired') reportSessionExpired();
      return fallback;
    }
  }, [reportSessionExpired]);

  const loadSelf = useCallback(async (): Promise<PortalSelf | null> => {
    try {
      const s = await getPortalSelf();
      setSelf(s);
      setLoadError(null);
      return s;
    } catch (err) {
      const info = describePortalError(err);
      if (info.kind === 'sessionExpired') reportSessionExpired();
      setLoadError(info);
      return null;
    }
  }, [reportSessionExpired]);

  const reload = useCallback(async () => {
    const today = localYmd(Date.now(), zone);
    setTodayYmd(today);
    const s = await loadSelf();
    if (!s) {
      setLoading(false);
      return;
    }
    const [ps, reqs, hols] = await Promise.all([
      guarded(() => getPortalPayPeriodsAround(today), [] as PortalPayPeriod[]),
      guarded(() => getPortalTimeOffRequests(), [] as PortalTimeOffRequest[]),
      guarded(() => getPortalHolidays(today, addDaysYmd(today, HOLIDAY_LOOKAHEAD_DAYS)), [] as PortalHoliday[]),
    ]);
    setPeriods(ps);
    setRequests(reqs);
    setHolidays(hols);

    const { current, previous } = currentAndPrevious(ps, today);
    const thisID = s.payPeriodID ?? current?.payPeriodID ?? null;
    const [thisStatus, lastStatus, lastRows] = await Promise.all([
      thisID ? guarded<PortalTimesheetStatus | null>(() => getPortalTimesheetStatus(thisID), null) : Promise.resolve(null),
      previous ? guarded<PortalTimesheetStatus | null>(() => getPortalTimesheetStatus(previous.payPeriodID), null) : Promise.resolve(null),
      previous ? guarded<PortalHourlyRow[]>(() => getPortalPeriodHourly(s.employeeID, previous.payPeriodID), []) : Promise.resolve([] as PortalHourlyRow[]),
    ]);
    setThisWeekStatus(thisStatus);
    setLastWeekStatus(lastStatus);
    setLastWeekHasTime(lastRows.length > 0);
    setLoading(false);
  }, [zone, loadSelf, guarded]);

  const reloadSelf = useCallback(async () => {
    const s = await loadSelf();
    const id = s?.payPeriodID ?? null;
    if (id) {
      const st = await guarded<PortalTimesheetStatus | null>(() => getPortalTimesheetStatus(id), null);
      if (st) setThisWeekStatus(st);
    }
  }, [loadSelf, guarded]);

  const reloadTimeOffRequests = useCallback(async () => {
    try {
      setRequests(await getPortalTimeOffRequests());
    } catch (err) {
      if (describePortalError(err).kind === 'sessionExpired') reportSessionExpired();
      throw err;
    }
  }, [reportSessionExpired]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const { current: currentPeriod, previous: previousPeriod } = useMemo(
    () => currentAndPrevious(periods, todayYmd),
    [periods, todayYmd],
  );

  const lastWeekOpen =
    !!previousPeriod && !!lastWeekStatus && !lastWeekStatus.submitted && !lastWeekStatus.exported && lastWeekHasTime;

  // Sidebar badges (handoff §Shell): amber "1" on My Timesheet while last week is open;
  // the pending time-off count on Time Off.
  useEffect(() => {
    setPortalBadges({ timesheet: lastWeekOpen ? 1 : 0, timeOff: pendingTimeOffCount(requests) });
  }, [lastWeekOpen, requests, setPortalBadges]);

  const onSessionRestored = useCallback((cb: () => void) => {
    restoredListeners.current.add(cb);
    return () => { restoredListeners.current.delete(cb); };
  }, []);

  // "Sign in again": a user click, so the browser allows the MSAL popup.
  const signInAgain = async () => {
    setSigningIn(true);
    setSignInFailed(false);
    const ok = await reacquireTokenInteractive();
    setSigningIn(false);
    if (!ok) {
      setSignInFailed(true);
      return;
    }
    setSessionExpired(false);
    setSessionNote(null);
    await reload();
    restoredListeners.current.forEach((cb) => cb());
  };

  const value: PortalData = {
    cfg,
    zone,
    todayYmd,
    self,
    loading,
    loadError,
    periods,
    currentPeriod,
    previousPeriod,
    thisWeekStatus,
    lastWeekStatus,
    lastWeekOpen,
    requests,
    holidays,
    reload,
    reloadSelf,
    reloadTimeOffRequests,
    sessionExpired,
    reportSessionExpired,
    onSessionRestored,
    timesheetIntent,
    openTimesheet,
    clearTimesheetIntent,
  };

  return (
    <PortalDataContext.Provider value={value}>
      {children}
      {sessionExpired && (
        <div className="etp etp-modal-bg" role="presentation">
          <div className="etp-modal" role="alertdialog" aria-modal="true" aria-labelledby="etp-session-title">
            <div className="modal-head">
              <div className="mi warn"><Lock /></div>
              <div>
                <h3 id="etp-session-title">Your session expired</h3>
                <div className="ms">Sign in again to keep using the portal</div>
              </div>
            </div>
            <div className="modal-body">
              <p className="mtext">
                Sign in again to keep using the portal. Nothing was lost{sessionNote ? <> — {sessionNote}</> : null}.
              </p>
              <p className="mtext dim">You'll come back to the same page.</p>
              <p className="mtext err" role="alert" style={{ visibility: signInFailed ? 'visible' : 'hidden' }}>
                Sign-in didn't finish. Allow the pop-up for this site and try again.
              </p>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn primary wbtn" onClick={signInAgain} disabled={signingIn}>
                <User />{signingIn ? 'Signing in…' : 'Sign in again'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PortalDataContext.Provider>
  );
};
